import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/bits";
import { IntegrationManual } from "@/components/IntegrationManual";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsPage() {
  const [keys, setKeys] = useState(null);
  const [keyDialog, setKeyDialog] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = () => api.get("/tenant/api-keys").then(({ data }) => setKeys(data)).catch(() => setKeys([]));
  useEffect(() => { load(); }, []);

  const createKey = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/tenant/api-keys", { name: keyName });
      setNewKey(data.api_key);
      setKeyDialog(false);
      setKeyName("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const revoke = async (keyId) => {
    try {
      await api.delete(`/tenant/api-keys/${keyId}`);
      toast.success("Chave revogada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!keys) return <Spinner />;

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integrações & API" subtitle="Conecte seus ERPs e CRMs à abertura automática de chamados" />

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-zinc-100 flex items-center gap-2"><KeyRound className="h-4 w-4 text-indigo-400" /> Chaves de API</h2>
          <Button data-testid="new-api-key-button" onClick={() => setKeyDialog(true)} size="sm"
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
            <Plus className="h-4 w-4 mr-1" /> Nova chave
          </Button>
        </div>
        <div className="flex justify-end mb-2">
          <button data-testid="toggle-revoked-keys" onClick={() => setShowRevoked(!showRevoked)}
            className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors duration-200">
            {showRevoked ? "Ocultar revogadas" : "Mostrar revogadas"}
          </button>
        </div>
        {keys.filter((k) => showRevoked || !k.revoked).length === 0 ? (
          <p className="text-sm text-zinc-400">Nenhuma chave ativa.</p>
        ) : (
          <div className="divide-y divide-zinc-800 border border-zinc-800 rounded-md">
            {keys.filter((k) => showRevoked || !k.revoked).map((k) => (
              <div key={k.id} className="flex items-center gap-3 px-4 py-3" data-testid={`api-key-row-${k.id}`}>
                <code className="font-mono text-xs text-zinc-300">{k.key_prefix}…</code>
                <span className="text-sm text-zinc-400">{k.name}</span>
                {k.revoked
                  ? <Badge className="border-0 text-[11px] text-rose-400 bg-rose-400/10 hover:bg-transparent">Revogada</Badge>
                  : <Badge className="border-0 text-[11px] text-emerald-400 bg-emerald-400/10 hover:bg-transparent">Ativa</Badge>}
                <span className="text-xs text-zinc-400 ml-auto">{k.last_used_at ? `último uso ${fmtDate(k.last_used_at)}` : "nunca usada"}</span>
                {!k.revoked && (
                  <Button data-testid={`revoke-key-${k.id}`} variant="ghost" size="icon" onClick={() => revoke(k.id)}
                    className="h-7 w-7 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
        <h2 className="text-lg font-medium text-zinc-100 mb-4">Manual de Integração</h2>
        <IntegrationManual />
      </div>

      <Dialog open={keyDialog} onOpenChange={setKeyDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Nova chave de API</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">A chave será exibida apenas uma vez após a criação.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createKey} className="space-y-4" data-testid="api-key-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome/identificação da chave</Label>
              <Input data-testid="api-key-name-input" required value={keyName} onChange={(e) => setKeyName(e.target.value)}
                placeholder="Ex.: Integração ERP"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="api-key-create-submit" type="submit" disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerar chave"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newKey} onOpenChange={() => setNewKey(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Chave gerada</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Guarde a chave em local seguro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">Copie agora — ela não será exibida novamente.</p>
            <code data-testid="new-api-key-value" className="block bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 font-mono text-xs text-emerald-400 break-all select-all">{newKey}</code>
            <Button data-testid="new-key-close" onClick={() => setNewKey(null)} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-md">Entendi, já copiei</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
